import { Drawer, Form } from "antd";
import { useRouter } from "next/router";
import { FC, useEffect } from "react";
import { JIRA_OX_APP_PROJECT_ID } from "../../../../../config/constants";
import { useGetCreateJiraIssueMetaQuery } from "../../../../../lib/api/endpoints/Jira/issuesEndpoints";
import { BUG_IMPACTS } from "../../../../../lib/types/shared/enums";
import { GStateAction } from "../../../../../lib/types/shared/reactInherited";
import { requiredField } from "../../../../../lib/validation/InputValidations";
import { G_Button } from "../../../Button";
import G_Div from "../../../Containers/G_Div";
import G_Image from "../../../Image/G_Image";
import RowInputs from "../../../RowInputs";
import { G_Text } from "../../../Text";
import { useCreateJiraIssue } from "./hooks";

interface IProps {
  open: boolean;
  setOpen: GStateAction<boolean>;
}
/**
 * @since September 2024
 * @author Issa Jean Marie <jeanmarieissa@gmail.com>
 * @see {@link https://issadevs.com} - Author's website
 */

const ReportBugDrawer: FC<IProps> = ({ open, setOpen }): JSX.Element => {
  const { data, isFetching } = useGetCreateJiraIssueMetaQuery();
  const router = useRouter();
  const traceId = router.query.traceId as string | undefined;
  const OX_APP = data?.projects?.find(
    (project) => project.id === JIRA_OX_APP_PROJECT_ID
  );

  const { onClose, form, onFinish, isLoading } = useCreateJiraIssue(setOpen);

  useEffect(() => {
    if (traceId && OX_APP?.issuetypes) {
      const bugType = OX_APP.issuetypes.find(
        (t) => t.name?.toLowerCase() === "bug"
      );
      if (bugType) form.setFieldsValue({ issuetype: bugType.id });
    }
  }, [traceId, OX_APP?.issuetypes, form]);

  return (
    <Drawer
      onClose={onClose}
      open={open}
      width={600}
      closable={false}
      bodyStyle={{ padding: "0" }}
    >
      <G_Div className="w-full p-10 px-12 bg-ox_2_primary flex items-center justify-between">
        <G_Div>
          <G_Text dimension="medium" weight="medium" mb={6}>
            Report a Bug
          </G_Text>
          <G_Text dimension="caption">
            These bugs are going straight to Jira
          </G_Text>
        </G_Div>

        <G_Image
          onClick={onClose}
          src="/icons/close-black.svg"
          width={14}
          pointer
          className="cursor-pointer"
        />
      </G_Div>

      <Form
        form={form}
        onFinish={onFinish}
        name="creat-jira-issue"
        className="p-10 px-12"
      >
        <RowInputs
          colsCount={1}
          gapCount={12}
          inputs={[
            {
              name: "issuetype",
              type: "select",
              label: "Issue type",
              isLoading: isFetching,
              placeholder: "Select issue type",
              rules: requiredField("Issue type is required"),
              options: OX_APP?.issuetypes
                ?.filter(
                  (type) =>
                    type?.name?.toLowerCase() === "bug" ||
                    type.name?.toLowerCase() === "story"
                )
                ?.map((issueType) => ({
                  key: issueType.name,
                  label: issueType.name,
                  value: issueType.id
                })),
              prefixIcon: (
                <G_Image
                  src="/icons/bug-primary.svg"
                  alt=""
                  height={18}
                  width={18}
                />
              )
            }
          ]}
        />

        <RowInputs
          colsCount={1}
          gapCount={12}
          inputs={[
            {
              name: "summary",
              type: "text",
              label: "Summary",
              rules: requiredField("Summary is required"),
              placeholder: "Type summary here",
              prefixIcon: (
                <G_Image
                  src="/icons/info-primary.svg"
                  alt=""
                  height={18}
                  width={18}
                />
              )
            }
          ]}
        />

        <RowInputs
          colsCount={1}
          gapCount={12}
          inputs={[
            {
              name: "description",
              type: "text_area",
              label: "Description",
              placeholder: "Describe the bug..."
            }
          ]}
        />

        <RowInputs
          colsCount={1}
          gapCount={12}
          inputs={[
            {
              name: "actionBeforeBug",
              type: "text_area",
              label: "Action before the bug",
              placeholder: "What did you do before the bug occurred?"
            }
          ]}
        />

        <RowInputs
          colsCount={2}
          gapCount={6}
          inputs={[
            {
              name: "personalImpact",
              type: "select",
              label: "Personal Impact",
              placeholder: "Select personal impact",
              options: BUG_IMPACTS.map((impact) => ({
                label: impact,
                key: impact,
                value: impact
              }))
            },
            {
              name: "businessImpact",
              type: "select",
              label: "Business Impact",
              placeholder: "Select business impact",
              options: BUG_IMPACTS.map((impact) => ({
                label: impact,
                key: impact,
                value: impact
              }))
            }
          ]}
        />

        <G_Div className="mt-6 w-full flex justify-end">
          <G_Button
            htmlType="submit"
            form="creat-jira-issue"
            loading={isLoading}
            iconSrc="/icons/bug-black.svg"
          >
            REPORT BUG
          </G_Button>
        </G_Div>
      </Form>
    </Drawer>
  );
};

export default ReportBugDrawer;
